"use client"

import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Check, ChevronDown, ChevronRight, Plus, Loader2 } from "lucide-react"
import type { ToolRenderProps } from "@/hooks/useToolRenderer"
import { useAuth } from "@/hooks/useAuth"
import { addToExtraction } from "@/services/extractionsService"
import { buildExtractionKey, useExtractions } from "../ExtractionsContext"
import { getUnitOfAnalysisLabel } from "@/utils/surveyUtils"

interface VariableValue {
  value: string
  valueDescription: string
}

interface SurveyVariable {
  programName: string
  surveyId: string
  variableName: string
  variableLabel: string
  s3URI?: string
  countryName: string
  surveyYear: string
  variableValues?: VariableValue[]
  /** MICS round identifier (only present for MICS surveys). */
  round?: string
  /** Unit of analysis code (recode for DHS/SPA, unitofanalysis for LSMS). */
  unitOfAnalysis?: string
}

/** Props for DescribeVariableWidget — extends ToolRenderProps with session context. */
interface DescribeVariableWidgetProps extends ToolRenderProps {
  /** The active chat session UUID, used as the partition key when storing extractions. */
  sessionId: string
  /** Called when a variable is successfully added to the extraction. */
  onExtractionAdded?: () => void
}

/**
 * Custom widget rendered inline in chat messages when the describe-survey-variable
 * tool is invoked. Displays variable metadata, a values table, and an
 * "Add To Extraction" button that persists the variable to DynamoDB.
 *
 * @param props - ToolRenderProps plus sessionId from the parent ChatInterface.
 * @returns A card UI with variable details and an extraction button, or null if
 *          the tool result cannot be parsed.
 */
export function DescribeVariableWidget({ result, sessionId, onExtractionAdded }: DescribeVariableWidgetProps) {
  const [showValues, setShowValues] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const auth = useAuth()
  // Shared extraction state — fetched once at the provider level so we don't
  // make one HTTP call per widget. Outside an ExtractionsProvider this hook
  // returns inert defaults (empty Set, no-op markAdded), keeping the widget
  // usable in chats that don't render the extractions panel.
  const { addedKeys, refresh } = useExtractions()

  // Parse the result JSON to extract variable data
  let variable: SurveyVariable | null = null
  if (result) {
    try {
      const parsed = JSON.parse(result)
      variable = parsed.surveyVariable ?? parsed
    } catch {
      // If parsing fails, fall back to null
    }
  }

  // If parsing failed, show nothing (the ToolCallDisplay above already shows the raw result)
  if (!variable) {
    return null
  }

  // If critical fields are missing, don't render the widget — the tool likely
  // failed to find a valid variable match.
  if (!variable.surveyId || !variable.variableName || !variable.variableLabel || !variable.s3URI) {
    return null
  }

  // Deduplicate value labels by value+description
  const uniqueValues = variable.variableValues?.filter(
    (v, i, arr) =>
      v.value !== "" &&
      arr.findIndex(x => x.value === v.value && x.valueDescription === v.valueDescription) === i
  ) ?? []

  // Non-categorical (numeric) variables have no value labels — an empty
  // uniqueValues list is the signal.
  const isCategorical: boolean = uniqueValues.length > 0

  // Look up whether this variable is already in the session's extraction
  // list using the shared context. This is a constant-time check against
  // the in-memory Set populated by ExtractionsProvider's single GET.
  const fileName: string = variable.s3URI
    ? variable.s3URI.split("/").pop() ?? ""
    : ""
  const isAlreadyAdded: boolean = addedKeys.has(
    buildExtractionKey({ surveyId: variable.surveyId, fileName: fileName, variableName: variable.variableName })
  )

  /**
   * Handles the "Add To Extraction" button click.
   * Sends the variable to the backend API for storage in DynamoDB.
   * If the variable already exists, the backend treats it as a no-op.
   */
  async function handleAddToExtraction(): Promise<void> {
    if (isAdding || !variable) {
      return
    }

    setIsAdding(true)

    try {
      const accessToken = await auth.getAccessToken()
      if (!accessToken) {
        throw new Error("Authentication required. Please log in again.")
      }

      await addToExtraction(
        {
          sessionId,
          surveyId: variable.surveyId,
          variableName: variable.variableName,
          programName: variable.programName,
          variableLabel: variable.variableLabel,
          fileName: fileName,
          s3URI: variable.s3URI ?? "",
          values: uniqueValues,
          countryName: variable.countryName,
          surveyYear: variable.surveyYear,
          round: variable.round,
          unitOfAnalysis: variable.unitOfAnalysis,
        },
        accessToken
      )

      setError(null)
      // Re-fetch the extraction list from DynamoDB so local state reflects
      // what's actually persisted. If the write landed, the variable shows
      // up in addedKeys and the button flips to "Added". If something
      // silently failed server-side, the button stays enabled.
      await refresh()
      onExtractionAdded?.()
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to add variable to extraction"
      setError(message)
      console.error("Failed to add variable to extraction:", err)
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <Card className="my-2 border-primary/20 shadow-sm w-full max-w-2xl">
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base font-semibold">
              {variable.variableName}
              <span className="ml-2 font-normal text-muted-foreground text-sm">
                {variable.variableLabel}
              </span>
            </CardTitle>
            <div className="flex flex-wrap gap-2 mt-1">
              <Badge variant="secondary">{variable.programName}</Badge>
              <Badge variant="outline">{variable.surveyId}</Badge>
              <Badge variant="outline">{variable.countryName}</Badge>
              <Badge variant="outline">{variable.surveyYear}</Badge>
              {variable.unitOfAnalysis && (
                <Badge variant="outline">{getUnitOfAnalysisLabel(variable.unitOfAnalysis, variable.programName)}</Badge>
              )}
            </div>
          </div>
          <Button
            size="sm"
            className="gap-1.5 shrink-0"
            onClick={handleAddToExtraction}
            disabled={isAdding || isAlreadyAdded}
          >
            {/*
              Three visual states:
                1. Currently adding -> spinner + "Add To Extraction"
                2. Already in the extraction list -> check icon + "Added"
                3. Not yet added -> plus icon + "Add To Extraction"
            */}
            {isAdding ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : isAlreadyAdded ? (
              <Check className="h-3.5 w-3.5" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            {isAlreadyAdded ? "Added" : "Add To Extraction"}
          </Button>
        </div>
        {error && (
          <p className="text-xs text-destructive mt-2">{error}</p>
        )}
      </CardHeader>

      {isCategorical ? (
        <CardContent className="px-4 pb-3 pt-0">
          <button
            onClick={() => setShowValues(!showValues)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
          >
            {showValues ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {uniqueValues.length} value {uniqueValues.length == 1 ? `category` : `categories`}
          </button>

          {showValues && (
            <div className="mt-2 max-h-60 overflow-y-auto rounded border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24 text-xs">Category</TableHead>
                    <TableHead className="text-xs">Description</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {uniqueValues.map((v, i) => (
                    <TableRow key={`${v.value}-${i}`}>
                      <TableCell className="font-mono text-xs py-1.5">{v.value}</TableCell>
                      <TableCell className="text-xs py-1.5">{v.valueDescription}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      ) : (
        // Non-categorical (numeric) variable: no discrete value labels exist,
        // so we surface a "labels" note instead of a "values" table.
        <CardContent className="px-4 pb-3 pt-0">
          <p className="text-xs text-muted-foreground py-1">
            No value labels - all values are non-categorical
          </p>
        </CardContent>
      )}
    </Card>
  )
}
